import axios from "@shared/axios";
import Session from "@shared-models/planning/session.model";
import UserSession from "@shared-models/planning/user.session.model";
import UserStatsDTO from "@shared-dto/planning/user.stats.dto";
import PlanningMarkerDayDTO from "@shared-dto/planning/planning.marker.day.dto";

async function getUserSessionBySessionId(sessionId: string): Promise<UserSession>
{
	const response = await axios.get(
		`/planning/user-sessions/by-session/${ sessionId }`,
	);

	return response?.data as UserSession ?? null;
}

async function registerToSession(session: Session)
{
	const response = await axios.post(
		`/planning/sessions/${ session.id }/register`
	);

	return response?.data ?? null;
}

async function unregisterFromSession(userSession: UserSession)
{
	const response = await axios.delete(
		`/planning/user-sessions/${ userSession.id }/unregister`
	);

	return response?.data ?? null;
}

async function updateWorkoutNotes(userSessionId: string, workoutNotes?: string | undefined | null)
{
	const response = await axios.put(
		`/planning/user-sessions/${ userSessionId }/workout-notes`,
		{
			"workout_notes": workoutNotes ?? null
		}
	);

	return response?.data ?? null;
}

async function updateNoteForCoach(userSessionId: string, noteForCoach: string | undefined | null)
{
	const response = await axios.put(
		`/planning/user-sessions/${ userSessionId }/note-for-coach`,
		{
			"note_for_coach": noteForCoach ?? null
		}
	);

	return response?.data ?? null;
}

async function getNextUserSessions(): Promise<UserSession[]>
{
	const response = await axios.get(
		"/planning/user-sessions/next"
	);

	return response?.data as UserSession[] ?? [];
}

/** Liste les séances passées de l'user avec au moins un bloc scoré non noté.
 *  Consommé par /workouts/scores (liste) + /workouts/score (redirect smart). */
async function getUserSessionsToScore(): Promise<UserSession[]>
{
	const response = await axios.get("/planning/user-sessions/to-score");
	return response?.data as UserSession[] ?? [];
}

/** Liste les UserSessions de l'user pour un set de dates ISO (typiquement les 7 jours d'une semaine).
 *  Consommé par le tab Séances (planning hebdo perso) — uniquement les sessions de l'user. */
async function getUserSessionsByDates(datesIso: string[]): Promise<UserSession[]>
{
	const response = await axios.get(
		"/planning/user-sessions/by-dates",
		{ params: { dates: datesIso } },
	);
	return response?.data as UserSession[] ?? [];
}

/** Liste les UserSessions de l'user pour tout un mois ("YYYY-MM"). Utilisé par la vue mensuelle
 *  /workouts pour peupler le calendrier — 1 seul appel HTTP au lieu de 30 dates en query. */
async function getUserSessionsByMonth(month: string): Promise<UserSession[]>
{
	const response = await axios.get(`/planning/user-sessions/by-month/${ month }`);
	return response?.data as UserSession[] ?? [];
}

async function getPlanningMarkersDaysByMonthIso(monthIso: string): Promise<PlanningMarkerDayDTO[]>
{
	const response = await axios.get(`/planning/markers-days-by-month/${ monthIso }`);
	return response?.data as PlanningMarkerDayDTO[] ?? null;
}

async function getStats(): Promise<UserStatsDTO>
{
	const response = await axios.get(
		"/planning/user-sessions/stats"
	);

	return response?.data as UserStatsDTO ?? null;
}

export {
	getUserSessionBySessionId,
	registerToSession,
	unregisterFromSession,
	updateWorkoutNotes,
	updateNoteForCoach,

	getNextUserSessions,
	getUserSessionsToScore,
	getUserSessionsByDates,
	getUserSessionsByMonth,
	getPlanningMarkersDaysByMonthIso,
	getStats,
};
